import { Box } from '@mui/system';
import React, { useCallback } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import useKeywordSearchRequestBuilder from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/KeywordsSearch/useKeywordSearchRequestBuilder';
import { SegmentDeletionModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentDeletionModal';
import SegmentNoDataPlaceholder from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentNoDataPlaceholder';
import { SegmentBuilderSidebar } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/SegmentBuilderSidebar';
import { BeaconProSidebarFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import { LegacyTopicBuilderSidebar } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/LegacyTopicBuilderSidebar';
import { StorageSource } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import { TopicBuilderSidebarV2 } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/TopicBuilderSidebarV2';
import { LegacyTopicBuilderCreateResults } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Visualizations/LegacyTopicBuilderCreateResults';
import { TopicBuilderCreateResults } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Visualizations/TopicBuilderCreateResults';
import {
  FILTER_OPERATION,
  useSavedSearchBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useSavedSearchBuilder';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import ProductListContainer from 'src/components/BeaconV2Components/MetricList/ProductListContainer';
import { BEACON_CHART_SPACING, BEACON_PRO_SIDEBAR_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { getQueryParamValue, shouldShowBeaconProTopics } from 'src/utils/app';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { RouteComponentProps } from 'react-router-dom';

interface EntityTypeTabsProps {
  selectedTab: 'segment' | 'searchtermlist';
  setTab: (selectedTab: 'segment' | 'searchtermlist') => void;
  availableTabs: (
    | {
        label: 'Product';
        value: 'segment';
      }
    | {
        label: 'Keyword';
        value: 'searchtermlist';
      }
  )[];
}
/**
 * List of tabs for the top of the entity builder page
 */
const EntityTypeTabs = ({ selectedTab, availableTabs, setTab }: EntityTypeTabsProps) => {
  return (
    <GenericNavigationTabs
      tab={selectedTab}
      tabs={availableTabs.map(({ label, value }) => {
        return {
          label,
          onClick: () => setTab(value),
          value
        };
      })}
    />
  );
};

/**
 * Renders a Product Grid with Segment Builder results.
 */
const ProductSearchResults = () => {
  const keywordSearchRequestBuilder = useKeywordSearchRequestBuilder('retailSales');

  const { updateSavedSearch } = useSavedSearchBuilder();

  // Handles quick exclude button click
  const handleExcludeButtonClick = (term: string) => {
    updateSavedSearch({
      operation: FILTER_OPERATION.Add,
      key: BeaconProSidebarFilters.ExcludedKeyword.filterKey,
      newValue: term
    });
  };
  return (
    <ProductListContainer
      productGridProps={{
        showExcludeProductButton: true,
        handleExcludeProduct: handleExcludeButtonClick,
        noDataPlaceholder: (
          <Flex justifyContent="center">
            <SegmentNoDataPlaceholder
              title="No results found"
              subtitle="Please refine your filters and try again."
              maxWidth={150}
            />
          </Flex>
        )
      }}
      fields={[
        {
          name: 'retailSales'
        },
        {
          name: 'unitsSold'
        },
        {
          name: 'retailPrice'
        }
      ]}
      showHeader={false}
      showGrid
      indexName="sales"
      sortFieldName="retailSales"
      useTableDataProps={{
        buildRequest: keywordSearchRequestBuilder()
      }}
    />
  );
};

export const SegmentTopicBuilderPage = ({ history }: RouteComponentProps) => {
  const entityType = getQueryParamValue('entityType');
  const entityIdBeingEdited = getQueryParamValue('id');
  const isTopicEntityType = entityType === 'searchtermlist';
  const isSearchResultsPage = !!getQueryParamValue('searchPage');
  const pageTitle = isSearchResultsPage ? 'Search Results' : isTopicEntityType ? 'Topic Builder' : 'Segment Builder';
  const availableTabs: EntityTypeTabsProps['availableTabs'] = isSearchResultsPage
    ? shouldShowBeaconProTopics()
      ? [
          {
            label: 'Product',
            value: 'segment'
          },
          {
            label: 'Keyword',
            value: 'searchtermlist'
          }
        ]
      : [
          {
            label: 'Product',
            value: 'segment'
          }
        ]
    : isTopicEntityType
    ? [{ label: 'Keyword', value: 'searchtermlist' }]
    : [{ label: 'Product', value: 'segment' }];

  const [deleteEntityModalOpen, setDeleteEntityModalOpen] = React.useState(false);
  const [entityToDelete, setEntityToDelete] = React.useState(null);

  const renderSidebar = useCallback(() => {
    switch (entityType) {
      case 'searchtermlist':
        if (shouldShowBeaconProTopics()) {
          return <TopicBuilderSidebarV2 source={StorageSource.DUPLICATE} />;
        } else {
          return <LegacyTopicBuilderSidebar />;
        }
      case 'segment':
        return <SegmentBuilderSidebar />;
      default:
        return null;
    }
  }, [entityType]);

  const renderBuilderResults = useCallback(() => {
    switch (entityType) {
      case 'searchtermlist':
        if (shouldShowBeaconProTopics()) {
          return (
            <div className="topicsBuilderSearchResult">
              <TopicBuilderCreateResults />{' '}
            </div>
          );
        } else {
          return <LegacyTopicBuilderCreateResults />;
        }
      case 'segment':
        return (
          <div style={{ position: 'relative', top: '-2px' }}>
            <ProductSearchResults />
          </div>
        );
      default:
        return null;
    }
  }, [entityType]);

  return (
    <BasePageContainer sx={{ marginTop: '0px', display: 'flex', gap: '64px', height: 'auto' }}>
      <Box sx={{ width: BEACON_PRO_SIDEBAR_WIDTH }}>{renderSidebar()}</Box>
      <Flex flexDirection="column" sx={{ width: '100%' }}>
        <Flex sx={{ height: '40px' }} alignItems="center" justifyContent="space-between">
          <Text
            variant="h2"
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '26px',
              lineHeight: '40px',
              letterSpacing: '0.08px'
            }}
          >
            {pageTitle}
          </Text>
          {entityIdBeingEdited && (
            <SlButton
              onClick={() => {
                setDeleteEntityModalOpen(true);
                setEntityToDelete(entityIdBeingEdited);
              }}
            >
              Delete
            </SlButton>
          )}
        </Flex>
        <EntityTypeTabs
          selectedTab={isTopicEntityType ? 'searchtermlist' : 'segment'}
          availableTabs={availableTabs}
          setTab={(newTab) => {
            const params = new URLSearchParams(window.location.search);
            params.set('entityType', newTab);
            history.push({ search: params.toString(), state: { searchPage: isSearchResultsPage } });
          }}
        />
        <div style={{ marginBottom: '32px' }}></div>
        <SegmentDeletionModal
          isTopicSegment={isTopicEntityType}
          segmentToDelete={entityToDelete}
          open={deleteEntityModalOpen}
          handleClose={() => {
            setDeleteEntityModalOpen(false);
            setEntityToDelete(null);
          }}
        />
        <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
          {renderBuilderResults()}
        </Flex>
      </Flex>
    </BasePageContainer>
  );
};
